// ─────────────────────────────────────────────────────────────────────────────
// Purchase Order — TypeScript interfaces  (Phase 65)
// ─────────────────────────────────────────────────────────────────────────────

export type POStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'PENDING_SENIOR_APPROVAL'
  | 'APPROVED'
  | 'ISSUED'
  | 'PARTIALLY_INVOICED'
  | 'CLOSED'
  | 'DECLINED';

export type POHistoryEvent =
  | 'CREATED'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'SENIOR_APPROVED'
  | 'DECLINED'
  | 'ISSUED'
  | 'INVOICE_MATCHED'
  | 'INVOICE_UNMATCHED'
  | 'CLOSED'
  | 'EDITED'
  | 'NOTE_ADDED';

export type POMatchType = 'AUTO' | 'MANUAL';

export type POEntryMode = 'CALCULATED' | 'FLAT_TOTAL';

export type POType = 'SPOT_BUY' | 'BLANKET';

// ── Core domain objects ───────────────────────────────────────────────────────

export interface POLineItem {
  id: number;
  po_id: number;
  line_number: number;
  description: string;
  gl_account: string | null;
  gl_description: string | null;
  job_number: string | null;
  quantity: number;
  unit_price: number;
  line_total: number;
  entry_mode: POEntryMode;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface POHistoryEntry {
  id: number;
  po_id: number;
  event_type: POHistoryEvent;
  actor_email: string | null;
  actor_name: string | null;
  note: string | null;
  old_status: POStatus | null;
  new_status: POStatus | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export interface POInvoiceMatch {
  id: number;
  po_id: number;
  po_line_id: number | null;
  invoice_id: number;
  invoice_number: string | null;
  matched_amount: number;
  match_type: POMatchType;
  matched_by_email: string | null;
  matched_at: string;
  unmatched_at: string | null;
  unmatched_by_email: string | null;
  active: boolean;
  notes: string | null;
}

export interface PurchaseOrder {
  id: number;
  po_number: string;
  fiscal_year: number;
  status: POStatus;

  requested_by_email: string;
  requested_by_name: string | null;
  requested_at: string;

  job_number: string;
  job_name: string | null;
  vendor_id: number | null;
  vendor_name: string;
  vendor_number: string | null;

  total_amount: number;
  invoiced_amount: number;
  currency: string;

  approver_email: string | null;
  approved_by_email: string | null;
  approved_at: string | null;

  senior_approver_email: string | null;
  senior_approved_by_email: string | null;
  senior_approved_at: string | null;

  declined_by_email: string | null;
  declined_at: string | null;
  decline_reason: string | null;

  issued_at: string | null;
  issued_by_email: string | null;
  po_pdf_blob_url: string | null;

  description: string | null;
  notes: string | null;
  notes_vendor: string | null;

  po_date: string | null;
  required_by_date: string | null;
  payment_terms: string | null;
  po_type: POType;

  ship_to_address: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip: string | null;
  ship_to_contact_name: string | null;
  ship_to_contact_phone: string | null;

  created_at: string;
  updated_at: string;
  active: boolean;

  // Populated by GET /mgmt/po/{id}
  lines?: POLineItem[];
  history?: POHistoryEntry[];
  matches?: POInvoiceMatch[];
}

// Summary row (returned by list endpoint — subset of PurchaseOrder)
export interface POSummary {
  id: number;
  po_number: string;
  fiscal_year: number;
  status: POStatus;
  requested_by_email: string;
  requested_by_name: string | null;
  requested_at: string;
  job_number: string;
  job_name: string | null;
  vendor_name: string;
  total_amount: number;
  invoiced_amount: number;
  approver_email: string | null;
  approved_at: string | null;
  senior_approver_email: string | null;
  senior_approved_at: string | null;
  issued_at: string | null;
  created_at: string;
  updated_at: string;
}

// ── Request / response shapes ─────────────────────────────────────────────────

export interface POLineItemInput {
  description: string;
  gl_account?: string;
  gl_description?: string;
  job_number?: string;
  quantity: number;
  unit_price: number;
  entry_mode?: POEntryMode;
  notes?: string;
}

export interface CreatePORequest {
  vendor_name: string;
  vendor_number?: string;
  vendor_id?: number;
  job_number: string;
  job_name?: string;
  description?: string;
  notes?: string;
  notes_vendor?: string;
  approver_email?: string;
  po_date?: string;
  required_by_date?: string;
  payment_terms?: string;
  po_type?: POType;
  ship_to_address?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  ship_to_contact_name?: string;
  ship_to_contact_phone?: string;
  lines: POLineItemInput[];
}

export interface UpdatePORequest {
  vendor_name?: string;
  vendor_number?: string;
  vendor_id?: number;
  job_number?: string;
  job_name?: string;
  description?: string;
  notes?: string;
  notes_vendor?: string;
  approver_email?: string;
  po_date?: string;
  required_by_date?: string;
  payment_terms?: string;
  po_type?: POType;
  ship_to_address?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  ship_to_contact_name?: string;
  ship_to_contact_phone?: string;
  lines?: POLineItemInput[];
}

export interface SubmitPOResponse {
  po_id: number;
  status: POStatus;
}

export interface ApproveDeclinePORequest {
  reason?: string;                  // for decline
  senior_approver_email?: string;   // for approve when threshold met
}

export interface MatchInvoiceRequest {
  invoice_id: number;
  po_line_id?: number;
  matched_amount: number;
  notes?: string;
}

export interface UnmatchInvoiceRequest {
  match_id: number;
}

export interface POListResponse {
  pos: POSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface POReviewToken {
  token: string;
  action: 'APPROVE' | 'DECLINE';
  reason?: string;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export const PO_STATUS_LABELS: Record<POStatus, string> = {
  DRAFT:                   'Draft',
  PENDING_APPROVAL:        'Pending Approval',
  PENDING_SENIOR_APPROVAL: 'Pending Sr. Approval',
  APPROVED:                'Approved',
  ISSUED:                  'Issued',
  PARTIALLY_INVOICED:      'Partially Invoiced',
  CLOSED:                  'Closed',
  DECLINED:                'Declined',
};

export const PO_STATUS_COLORS: Record<POStatus, string> = {
  DRAFT:                   'bg-gray-100 text-gray-700',
  PENDING_APPROVAL:        'bg-yellow-100 text-yellow-800',
  PENDING_SENIOR_APPROVAL: 'bg-orange-100 text-orange-800',
  APPROVED:                'bg-blue-100 text-blue-800',
  ISSUED:                  'bg-green-100 text-green-800',
  PARTIALLY_INVOICED:      'bg-teal-100 text-teal-800',
  CLOSED:                  'bg-slate-100 text-slate-600',
  DECLINED:                'bg-red-100 text-red-700',
};

export const PO_ACTIVE_STATUSES: POStatus[] = [
  'PENDING_APPROVAL',
  'PENDING_SENIOR_APPROVAL',
  'APPROVED',
  'ISSUED',
  'PARTIALLY_INVOICED',
];

export const PO_TERMINAL_STATUSES: POStatus[] = ['CLOSED', 'DECLINED'];

// Roles that can create/edit POs (used by frontend to gate UI)
export const PO_REQUESTOR_ROLES = ['ADMIN', 'POWER_USER', 'PO_REQUESTOR'];

// Roles that can approve POs (first-tier)
export const PO_APPROVER_ROLES = ['ADMIN', 'POWER_USER', 'PO_APPROVER', 'PO_SENIOR_APPROVER'];

// Roles that can senior-approve POs (second-tier / high-value)
export const PO_SENIOR_APPROVER_ROLES = ['ADMIN', 'POWER_USER', 'PO_SENIOR_APPROVER'];

// Roles that can issue/close POs (AP staff)
export const PO_AP_ROLES = ['ADMIN', 'POWER_USER'];

// Payment terms options for dropdown
export const PAYMENT_TERMS_OPTIONS = [
  'Net 30',
  'Net 60',
  'Net 90',
  'Due on Receipt',
  'COD',
];
